import React, { useState } from "react";
import jsPDF from "jspdf";
import logoImg from "../../../assets/images/newAdminLogoLive.png"; // Use PNG logo for compatibility
import { API_URL } from "../../../constant";
import axiosInstance from "../../../utils/axiosConfig";
import toast from "react-hot-toast";
import { TailSpin } from "react-loader-spinner";

interface AmlUserDetailsModalProps {
  open: boolean;
  onClose: () => void;
  userData: any;
  setRefetch: React.Dispatch<React.SetStateAction<number>>;
}

const AmlUserDetailsModal: React.FC<AmlUserDetailsModalProps> = ({
  open,
  onClose,
  userData,
  setRefetch,
}) => {
  const [approvalLoader, setApprovalLoader] = useState(false);
  if (!open || !userData) return null;

  const dummyChecks = [
    {
      label: "ID Verification",
      result: userData?.kycStatus?.kyc3 ? "No Risk Found" : "Risk Found",
    },
    {
      label: "PEP Screening",
      result: userData?.pepMatch ? "Risk Found" : "No Risk Found",
    },
    {
      label: "Sanction Check",
      result: userData?.sanctionsMatch ? "Risk Found" : "No Risk Found",
    },
    { label: "Adverse Media Check", result: "Risk Found" },
    {
      label: "IP & Country Risk",
      result: userData?.highRiskCountry ? "Risk Found" : "No Risk Found",
    },
  ];

  const dummyKyc = [
    {
      level: 1,
      status: userData?.kycStatus?.kyc1 ? "Completed" : "Not Completed",
    },
    {
      level: 2,
      status: userData?.kycStatus?.kyc2 ? "Completed" : "Not Completed",
    },
    {
      level: 3,
      status: userData?.kycStatus?.kyc3 ? "Completed" : "Not Completed",
    },
  ];

  const handleDownloadPDF = (userData: any) => {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    // Set dark background
    doc.setFillColor(31, 31, 31);
    doc.rect(0, 0, 210, 297, "F");

    // Watermark logo (center, faded)
    doc.addImage(logoImg, "PNG", 35, 80, 140, 40, undefined, "FAST");
    doc.setTextColor(255, 255, 255);

    // Overlay a rectangle for watermark effect (simulate opacity)
    doc.setFillColor(31, 31, 31);
    doc.setDrawColor(31, 31, 31);
    doc.rect(0, 0, 210, 297, "F");

    // Logo at the top
    doc.addImage(logoImg, "PNG", 15, 1, 40, 40, undefined, "FAST"); //make it a square

    // Use Sora font if available, else fallback
    // jsPDF does not support Sora by default, so we use a modern sans-serif
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.text("User AML Details", 60, 25);

    // User Info
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    let y = 60;
    doc.text(
      `First Name: ${userData?.userId?.profile?.firstName ?? "----"}`,
      15,
      y
    );
    y += 8;
    doc.text(
      `Last Name: ${userData?.userId?.profile?.lastName ?? "----"}`,
      15,
      y
    );
    y += 8;
    doc.text(`Email: ${userData?.userId?.email?.address ?? "----"}`, 15, y);
    y += 8;
    doc.text(`Country: ${userData?.userId?.profile?.country ?? "----"}`, 15, y);
    y += 8;
    doc.text(
      `Profile Updated At: ${
        userData?.userId?.profile?.profileUpdatedAt ?? "----"
      }`,
      15,
      y
    );
    y += 8;
    doc.text(`User ID: ${userData?.userId?._id ?? "----"}`, 15, y);
    y += 8;
    doc.text(
      `Migrated To Host Capital: ${
        userData?.userId?.migratedToHostCapital ? "Yes" : "No"
      }`,
      15,
      y
    );
    y += 8;
    doc.text(`KYC Status: ${userData?.userId?.kyc?.status ?? "----"}`, 15, y);
    y += 8;
    doc.text(
      `Push Tokens: ${userData?.userId?.pushTokens?.length ?? 0}`,
      15,
      y
    );
    y += 8;
    doc.text(`Risk Tier: ${userData?.riskTier ?? "----"}`, 15, y);
    y += 8;
    doc.text(`Risk Score: ${userData?.riskScore ?? "----"}`, 15, y);
    y += 8;
    doc.text(`PEP Match: ${userData?.pepMatch ? "Yes" : "No"}`, 15, y);
    y += 8;
    doc.text(
      `Sanctions Match: ${userData?.sanctionsMatch ? "Yes" : "No"}`,
      15,
      y
    );
    y += 8;
    doc.text(
      `High Risk Country: ${userData?.highRiskCountry ? "Yes" : "No"}`,
      15,
      y
    );
    y += 8;
    doc.text(`Country (Root): ${userData?.country ?? "----"}`, 15, y);
    y += 8;
    doc.text(`Created At: ${userData?.createdAt ?? "----"}`, 15, y);
    y += 8;
    doc.text(`Updated At: ${userData?.updatedAt ?? "----"}`, 15, y);
    y += 8;
    doc.text(`Mongo _id: ${userData?._id ?? "----"}`, 15, y);
    y += 8;
    doc.text(`__v: ${userData?.__v ?? "----"}`, 15, y);

    // KYC Status
    y += 10;
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("KYC Levels", 15, y);
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    y += 8;
    doc.text(
      `Level 1: ${userData?.kycStatus?.kyc1 ? "Completed" : "Not Completed"}`,
      20,
      y
    );
    y += 8;
    doc.text(
      `Level 2: ${userData?.kycStatus?.kyc2 ? "Completed" : "Not Completed"}`,
      20,
      y
    );
    y += 8;
    doc.text(
      `Level 3: ${userData?.kycStatus?.kyc3 ? "Completed" : "Not Completed"}`,
      20,
      y
    );

    // Footer
    doc.setFontSize(10);
    doc.setTextColor(180, 180, 180);
    doc.text("Generated with ❤️ by Deexoptions Admin", 15, 287);

    doc.save(
      `${userData?.userId?.profile?.firstName}-${userData?.userId?._id}.pdf`
    );
  };

  const clearUser = async () => {
    setApprovalLoader(true);
    try {
      await axiosInstance.put(`${API_URL}api/aml/logs/${userData?._id}`, {});
      toast.success("User cleared successfully");
    } catch (error: any) {
      toast.error(error.response?.data?.message || "An error occurred");
    } finally {
      setApprovalLoader(false);
      setRefetch((prev: number) => prev++);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 font-manrope">
      <div className="w-full max-w-lg">
        {/* Header & User Info */}
        <div className="bg-[#0D0D0D] rounded-2xl p-8 w-full shadow-lg relative">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-white text-xl font-bold">User Details</h2>
            <button
              onClick={onClose}
              className="text-[#78C2F7] text-base font-semibold"
            >
              Close
            </button>
          </div>
          <div className="flex items-center gap-4 mb-4">
            <div>
              <div className="text-[#DEE1E6] font-sora text-[24px] font-bold">
                {userData?.user?.profile?.firstName ?? "----"}{" "}
                {userData?.user?.profile?.lastName ?? "----"}
              </div>
              <div className="text-secondary_button text-[13px] font-medium">
                {userData?.user?.email?.address ?? "----"}
              </div>
            </div>
          </div>
          <div className="flex w-4/5 gap-4 mb-4">
            <button
              className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border border-[#3A3A3A] bg-[#23282D] text-[#BDBDBE] text-lg font-semibold hover:bg-[#23282D]/80"
              onClick={() => handleDownloadPDF(userData)}
            >
              <span className="text-xl">&#8681;</span> Download
            </button>
            {userData?.riskTier.toLowerCase() !== "low" && (
              <button
                onClick={clearUser}
                className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg bg-[#1ED760] text-[#181B1F] text-lg font-semibold hover:bg-[#1ED760]/90"
              >
                {approvalLoader ? (
                  <TailSpin width={20} height={20} color="#181B1F" />
                ) : (
                  <span className="text-xl">✔️</span>
                )}
                Clear User
              </button>
            )}
          </div>
        </div>
        {/* KYC Section */}
        <div className="bg-[#0D0D0D] rounded-2xl p-6 mt-4">
          <div className="text-white text-lg font-bold mb-2">KYC</div>
          {dummyKyc.map((kyc) => (
            <div
              key={kyc.level}
              className="flex justify-between items-center text-sm mb-1"
            >
              <span className="text-[#AEBCC6] text-[12px] font-medium">
                Level {kyc.level}
              </span>
              <span
                className={
                  kyc.status === "Completed"
                    ? "text-[#008FDF] text-[13px] font-bold"
                    : "text-[#AEBCC6] text-[13px] font-bold"
                }
              >
                {kyc.status}
              </span>
            </div>
          ))}
        </div>
        {/* Checks Section */}
        <div className="bg-[#0D0D0D] rounded-2xl p-6 mt-4">
          <div className="text-white text-lg font-bold mb-2">
            Checks (
            {
              dummyChecks.filter((check) => check.result === "Risk Found")
                .length
            }{" "}
            Risk
            {dummyChecks.filter((check) => check.result === "Risk Found")
              .length > 1
              ? "s"
              : " "}{" "}
            found)
          </div>
          {dummyChecks.map((check) => (
            <div
              key={check.label}
              className="flex justify-between items-center text-sm mb-2"
            >
              <span className="text-[#AEBCC6] text-[12px] font-bold">
                {check.label}
              </span>
              <span
                className={
                  check.result === "Risk Found"
                    ? "text-[#03C668] text-[13px] font-bold"
                    : "text-[#AEBCC6] text-[13px] font-bold"
                }
              >
                {check.result}
              </span>
            </div>
          ))}
        </div>
        {/* Resolution Section */}
        <div className="bg-[#0D0D0D] rounded-2xl p-6 mt-4">
          <div className="text-white text-lg font-bold mb-2">Resolution</div>
          <div className="flex items-center border-b border-[#23282D] pb-4 justify-between gap-4 mb-2">
            <span
              className={` text-[white] px-2 text-[11px] py-1 rounded-full text-xs font-bold ${
                userData?.riskTier.toLowerCase() === "high"
                  ? "bg-[#C60303]"
                  : userData?.riskTier.toLowerCase() === "medium"
                  ? "bg-[#134A7C]"
                  : userData?.riskTier.toLowerCase() === "low"
                  ? "bg-[#03C668]"
                  : ""
              }`}
            >
              {userData?.riskTier.toUpperCase()}
            </span>
            <span
              className={` text-lg font-bold ${
                userData?.riskTier.toLowerCase() === "high"
                  ? "text-[#C60303]"
                  : userData?.riskTier.toLowerCase() === "medium"
                  ? "text-[#134A7C]"
                  : userData?.riskTier.toLowerCase() === "low"
                  ? "text-[#03C668]"
                  : ""
              }`}
            >
              {userData?.riskTier.toLowerCase() === "high"
                ? "Rejected"
                : userData?.riskTier.toLowerCase() === "medium"
                ? "Suspicious"
                : userData?.riskTier.toLowerCase() === "low"
                ? "Approved"
                : ""}
            </span>
          </div>
          <div className="text-[#AEBCC6] text-[12px] font-semibold">
            Alert Compliance Team
          </div>
        </div>
      </div>
    </div>
  );
};

export default AmlUserDetailsModal;
